import { BaseButton } from 'app/components/buttons/BaseButton';
import { ChevronRightIcon, GlobeIcon } from 'app/components/icons';
import clsx from 'clsx';
import { useLanguageContext } from 'context';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './LanguageSwitcher.less';
import { BasePopup } from 'app/components/BasePopup';

interface Props {
  className?: string;
  hidden?: boolean;
}

export default function LanguageSwitcher({ className, hidden }: Props) {
  const { t } = useTranslation();
  const { language, changeLanguage } = useLanguageContext();

  const [anchorEl, setAnchorEl] = useState<HTMLElement>();

  const handleMenuClose = useCallback(() => {
    setAnchorEl(undefined);
  }, [anchorEl]);

  const handleMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (!!anchorEl) {
        handleMenuClose();
      } else {
        const element = event.currentTarget;
        setAnchorEl(element);
      }
    },
    [anchorEl, handleMenuClose],
  );

  useEffect(() => {
    if (hidden) {
      handleMenuClose();
    }
  }, [hidden, handleMenuClose]);

  const handleChangeLanguage = useCallback(
    (e: React.MouseEvent<HTMLLIElement, MouseEvent>, lang: string) => {
      e.preventDefault();
      e.stopPropagation();

      changeLanguage(lang);
      handleMenuClose();
    },
    [handleMenuClose, changeLanguage],
  );

  return (
    <>
      <BaseButton
        className={clsx('language-switcher__button', className)}
        onClick={handleMenuOpen}
      >
        <GlobeIcon />
        {language.toLocaleUpperCase().slice(0, 2)}
        <ChevronRightIcon
          className={clsx('language-switcher__chevron', {
            'language-switcher__chevron_rotated': !!anchorEl,
          })}
          height={24}
          width={24}
        />
      </BaseButton>

      <BasePopup
        className="language-switcher__popup"
        anchor={anchorEl}
        appendToAnchor
        onClose={handleMenuClose}
      >
        <ul className="language-switcher__list">
          <li
            className={clsx('language-switcher__list-item', {
              'language-switcher__list-item_current': language === 'en',
            })}
            onClick={e => handleChangeLanguage(e, 'en')}
          >
            {t('language_EN')}
          </li>

          <li
            className={clsx('language-switcher__list-item', {
              'language-switcher__list-item_current': language === 'fr',
            })}
            onClick={e => handleChangeLanguage(e, 'fr')}
          >
            {t('language_FR')}
          </li>
        </ul>
      </BasePopup>
    </>
  );
}
